import type { ActionResult } from "@magic-md/plugin-sdk";
import type {
  ContentExecutionEntry,
  ExecutionEntry,
  ForExecutionEntry,
} from "./execution-state";

export type ImportedSourceUnitKind = "node" | "content" | "for";

export interface RunImportedSourceUnitOptions {
  renderedUnitId: string;
  importNamespace: string;
  sourceDocumentPath: string;
  sourceUnitId: string;
  unitKind?: ImportedSourceUnitKind;
  pluginName?: string;
  actionName?: string;
  actionParams?: Record<string, unknown>;
}

export interface ImportedSourceExecutionState {
  executions: Map<string, ExecutionEntry>;
  contentExecutions: Map<string, ContentExecutionEntry>;
  forExecutions: Map<string, ForExecutionEntry>;
  executionError: string | null;
}

export function markImportedSourceRenderedUnitRunning(
  state: ImportedSourceExecutionState,
  options: RunImportedSourceUnitOptions,
  actionName: string,
  startedAt: number,
): Partial<ImportedSourceExecutionState> {
  const kind = options.unitKind ?? "node";
  if (kind === "content") {
    const next = new Map(state.contentExecutions);
    next.set(options.renderedUnitId, {
      contentId: options.renderedUnitId,
      status: "running",
      startedAt,
      duration: null,
      output: next.get(options.renderedUnitId)?.output,
    });
    return { contentExecutions: next, executionError: null };
  }
  if (kind === "for") {
    const next = new Map(state.forExecutions);
    const prev = next.get(options.renderedUnitId);
    next.set(options.renderedUnitId, {
      forId: options.renderedUnitId,
      status: "running",
      totalIterations: prev?.totalIterations ?? 0,
      completedIterations: prev?.completedIterations ?? 0,
      iterations: prev?.iterations ?? [],
      startedAt,
      duration: null,
    });
    return { forExecutions: next, executionError: null };
  }

  const next = new Map(state.executions);
  const prev = next.get(options.renderedUnitId);
  let cleanOutput = prev?.output ?? null;
  if (cleanOutput?.__error) {
    const { __error: _, ...rest } = cleanOutput;
    cleanOutput = rest as typeof cleanOutput;
  }
  next.set(options.renderedUnitId, {
    unitId: options.renderedUnitId,
    status: "running",
    actionName,
    result: null,
    output: cleanOutput,
    startedAt,
    duration: null,
  });
  return { executions: next, executionError: null };
}

export function markImportedSourceRenderedUnitError(
  state: ImportedSourceExecutionState,
  options: RunImportedSourceUnitOptions,
  actionName: string,
  errMsg: string,
  now = Date.now(),
): Partial<ImportedSourceExecutionState> {
  const kind = options.unitKind ?? "node";
  if (kind === "content") {
    const next = new Map(state.contentExecutions);
    const prev = next.get(options.renderedUnitId);
    next.set(options.renderedUnitId, {
      contentId: options.renderedUnitId,
      status: "error",
      startedAt: prev?.startedAt ?? null,
      duration: prev?.startedAt ? now - prev.startedAt : null,
      output: prev?.output,
      error: errMsg,
    });
    return { contentExecutions: next };
  }
  if (kind === "for") {
    const next = new Map(state.forExecutions);
    const prev = next.get(options.renderedUnitId);
    next.set(options.renderedUnitId, {
      forId: options.renderedUnitId,
      status: "error",
      totalIterations: prev?.totalIterations ?? 0,
      completedIterations: prev?.completedIterations ?? 0,
      iterations: prev?.iterations ?? [],
      startedAt: prev?.startedAt ?? null,
      duration: prev?.startedAt ? now - prev.startedAt : null,
      error: errMsg,
    });
    return { forExecutions: next };
  }

  const next = new Map(state.executions);
  const prev = next.get(options.renderedUnitId);
  const startedAt = prev?.startedAt ?? now;
  next.set(options.renderedUnitId, {
    unitId: options.renderedUnitId,
    status: "error",
    actionName,
    result: { success: false, message: errMsg },
    output: prev?.output ?? null,
    startedAt,
    duration: now - startedAt,
  });
  return { executions: next };
}

export function getImportedRenderedNodeResult(
  state: Pick<ImportedSourceExecutionState, "executions">,
  renderedUnitId: string,
): ActionResult {
  const entry = state.executions.get(renderedUnitId);
  if (!entry) {
    return {
      success: false,
      message: `Imported output not found: ${renderedUnitId}`,
    };
  }
  return {
    success: entry.status === "success",
    message: entry.result?.message,
    output: entry.output ?? undefined,
  };
}

export function isImportedRenderedUnitStillRunning(
  state: Pick<
    ImportedSourceExecutionState,
    "executions" | "contentExecutions" | "forExecutions"
  >,
  options: RunImportedSourceUnitOptions,
): boolean {
  const kind = options.unitKind ?? "node";
  if (kind === "content") {
    return (
      state.contentExecutions.get(options.renderedUnitId)?.status === "running"
    );
  }
  if (kind === "for") {
    return (
      state.forExecutions.get(options.renderedUnitId)?.status === "running"
    );
  }
  return state.executions.get(options.renderedUnitId)?.status === "running";
}
